import React, { useRef, useState } from 'react';
import { useTranslation } from '../../utils/i18nStub';

const EmployeeBankInfo = () => {
  const { t } = useTranslation('bank');
  const fileRef = useRef(null);
  const [bankName, setBankName] = useState('');
  const [iban, setIban] = useState('');
  const [ibanProofName, setIbanProofName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot] = useState({ bankName, iban, ibanProofName, confirmed });

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setIbanProofName('');
      return;
    }
    setIbanProofName(file.name);
  };

  const triggerFileDialog = () => {
    if (!isEditing) return;
    fileRef.current?.click();
  };

  const startEdit = () => {
    setSnapshot({ bankName, iban, ibanProofName, confirmed });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setBankName(snapshot.bankName);
    setIban(snapshot.iban);
    setIbanProofName(snapshot.ibanProofName);
    setConfirmed(snapshot.confirmed);
    setIsEditing(false);
  };

  const handleSave = () => {
    setSnapshot({ bankName, iban, ibanProofName, confirmed });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6 text-right" dir="rtl">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {t('bank.confirm_title', 'تأكيد معلومات البنك ورقم الآيبان')}
        </h2>
        {!isEditing ? (
          <button
            type="button"
            onClick={startEdit}
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('edit', 'تعديل')}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-sa-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors"
            >
              {t('save', 'حفظ')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('cancel', 'إلغاء')}
            </button>
          </div>
        )}
      </div>

      <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">
              {t('bank.bank_name', 'اسم البنك')}
            </label>
            <input
              type="text"
              value={bankName}
              onChange={(event) => setBankName(event.target.value)}
              placeholder={t('bank.bank_name_placeholder', 'مثال: بنك الراجحي')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">
              {t('bank.iban', 'رقم الآيبان (IBAN)')}
            </label>
            <input
              type="text"
              value={iban}
              onChange={(event) => setIban(event.target.value.toUpperCase())}
              placeholder={t('bank.iban_placeholder', 'SA00 0000 0000 0000 0000 0000')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-left text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
              dir="ltr"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-500 mb-2">
            {t('bank.iban_proof', 'إرفاق مستند يثبت رقم الآيبان')}
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center bg-gray-50">
            <p className="text-sm text-gray-600 mb-4">
              {t('bank.iban_proof_hint', 'يمكنك إرفاق كشف حساب أو خطاب رسمي يظهر فيه رقم الآيبان مع شعار البنك.')}
            </p>
            <button
              type="button"
              onClick={triggerFileDialog}
              className="inline-flex items-center gap-2 rounded-xl bg-sa-600 text-white px-5 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors disabled:opacity-50"
              disabled={!isEditing}
            >
              {t('bank.upload_button', 'اختيار ملف')}
            </button>
            {ibanProofName && (
              <p className="mt-3 text-sm text-gray-700">
                {t('bank.selected_file', 'الملف المحدد')}: {ibanProofName}
              </p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.png,.jpeg"
              className="hidden"
              disabled={!isEditing}
              onChange={handleFilePick}
            />
          </div>
        </div>

        <div className="flex items-start gap-3 bg-sa-50 border border-sa-100 rounded-2xl p-4">
          <input
            id="confirm-bank"
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            disabled={!isEditing}
            className="mt-1 h-5 w-5 rounded border-gray-300 text-sa-600 focus:ring-sa-500"
          />
          <label htmlFor="confirm-bank" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
            {t('bank.confirm_text', 'أتعهد بأن جميع بيانات البنك والآيبان صحيحة ومطابقة لوثائق البنك الرسمية.')}
          </label>
        </div>

        {isEditing && (
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('bank.cancel', 'إلغاء')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-sa-600 text-white px-5 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors disabled:opacity-50"
              disabled={!bankName || !iban || !confirmed}
            >
              {t('bank.save', 'حفظ المعلومات')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default EmployeeBankInfo;
